import {
  DocumentProcessingStatus,
  isDocumentProcessing,
} from '@folio/components/features/document-viewer/DocumentProcessingStatus';
import { DocumentViewer } from '@folio/components/features/document-viewer/DocumentViewer';
import { useDocumentViewer } from '@folio/components/features/document-viewer/useDocumentViewer';
import type { DocumentViewerDocument } from '@folio/components/features/document-viewer/types';
import { useAssertedParams } from '@folio/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  brokerageFirmControllerListDocumentsQueryOptions,
  brokerageFirmDocumentControllerGetBrokerageFirmDocumentConformityQueryOptions,
  brokerageFirmDocumentControllerGetBrokerageFirmDocumentFraudQueryOptions,
  brokerageFirmDocumentExtractionControllerGetBrokerageFirmExtractionsQueryOptions,
  brokerageFirmDocumentValidityControllerGetBrokerageFirmValidityQueryOptions,
  documentCoherenceControllerGetDocumentCoherenceQueryOptions,
  useDocumentExtractionControllerSubmitExtractionFeedback,
  useBrokerageFirmDocumentControllerAcceptBrokerageFirmDocument,
  useBrokerageFirmDocumentControllerAcceptAllBrokerageFirmDocuments,
  useBrokerageFirmDocumentControllerCancelBrokerageFirmDocumentConformity,
  useBrokerageFirmDocumentControllerCorrectBrokerageFirmDocumentFraudVerdict,
  useBrokerageFirmDocumentControllerRejectBrokerageFirmDocument,
  useBrokerageFirmDocumentValidityControllerOverrideDocumentValidity,
  getKorintErrorStatus,
  keyValueFieldsToObject,
  uploadDocumentsOnBrokerageFirm,
  useBrokerageFirmControllerDeleteDocuments,
  type CorrectPolicyDocumentFraudVerdictInputBody,
  type DocumentValidityRuleDto,
  type GetDocumentExtractionOutput,
  type SubmitFeedbackBody,
} from 'korint-sdk';
import { useDocumentConformityMap } from '@folio/hooks';
import {
  useDocumentsAutoClassification,
  getStoredSession,
  useFeatureFlag,
  type AIValidityResult,
} from 'korint-sdk/react';
import { useCallback, useMemo } from 'react';
import { AlertCircleIcon } from 'lucide-react';
import { useToast } from '../../components';
import { DocumentsCollection } from '../../components/module-elements/Documents/DocumentsCollection';
import { getUploadedDocumentGroups } from '../../components/module-elements/Documents/uploaded-document-groups';
import { translateBrokerageFirmDocument } from '../../utils/getDocumentCategoryLabel';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { useKorintConfig } from 'korint-sdk/react';
import { isUnresolvedCategory } from '@folio/utils/document-categories';
import { t } from 'i18next';
import { ConformityAcceptAllControl } from '../DocumentsListsPolicyModule/DocumentsListsPolicyModule';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type DocumentsUploadBrokerageFirmModuleProps = {
  /**
   * The title of the card.
   */
  title: string;

  /**
   * Custom subtitle for the card.
   */
  customCardSubtitle?: string;

  /**
   * The list of documents to be displayed in the module.
   */
  documentsLists: {
    category: string;
    required?: boolean;
    allowMultiple?: boolean;
    validity?: DocumentValidityRuleDto[];
  }[];

  /**
   * The function to be called when a document is uploaded.
   */
  onUpload?: () => void;

  /**
   * The upload mode: 'category' shows separate inputs per category,
   * 'batch' shows a single input for all documents with classification after upload.
   */
  uploadMode?: 'category' | 'batch';

  /**
   * The authorized formats for the documents.
   */
  authorizedFormats?: Record<string, string[]>;

  /**
   * Product ID for AI classification. When not provided, AI classification is not triggered.
   */
  productId?: string;

  /**
   * Shows fraud results to reviewers of a child firm. Never set on the firm's own screens.
   */
  showFraud?: boolean;
};

export function DocumentsUploadBrokerageFirmModule({
  customCardSubtitle,
  title,
  documentsLists,
  onUpload,
  authorizedFormats,
  productId,
  showFraud = false,
}: DocumentsUploadBrokerageFirmModuleProps) {
  const { brokerageFirmId } = useAssertedParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { clientOptions, korintConfig } = useKorintConfig();
  const aiClassificationDisplayConfig =
    korintConfig.modulesConfig?.aiClassification?.interfaces?.brokerageFirmDocuments;
  // Only used by single-tenant apps, where tenant is always set.
  const getAccessToken = useCallback(
    () => getStoredSession(clientOptions.tenant ?? '')?.accessToken,
    [clientOptions.tenant],
  );

  // Always use the batch/funnel-style rendering on the dashboard.
  const uploadMode = 'batch' as const;

  const relevantCategories = documentsLists.map(doc => doc.category);

  const queryOptions = brokerageFirmControllerListDocumentsQueryOptions(brokerageFirmId, {
    pageInfo: { count: 100 },
  });

  const { data: allDocuments } = useQuery({
    ...queryOptions,
    select: data =>
      data.data.map(document => ({
        ...document,
        metadata: keyValueFieldsToObject(document.metadata),
      })),
  });

  const documentConformityFlagEnabled = useFeatureFlag('feat_document_conformity');

  // Brokerage firms have no branch concept, unlike policies.
  const {
    data: conformityData,
    isSuccess: isDocumentReviewEnabled,
    refetch: refetchConformity,
  } = useQuery({
    ...brokerageFirmDocumentControllerGetBrokerageFirmDocumentConformityQueryOptions(
      brokerageFirmId,
    ),
    retry: false,
    enabled: documentConformityFlagEnabled,
  });

  const { conformityByDocumentId, conformityIdByDocumentId } = useDocumentConformityMap(
    allDocuments,
    conformityData?.conformities,
    isDocumentReviewEnabled,
  );

  const { mutate: acceptDocumentConformityMutation } =
    useBrokerageFirmDocumentControllerAcceptBrokerageFirmDocument({
      mutation: { onSuccess: () => refetchConformity() },
    });
  const { mutate: rejectDocumentConformityMutation } =
    useBrokerageFirmDocumentControllerRejectBrokerageFirmDocument({
      mutation: { onSuccess: () => refetchConformity() },
    });
  const { mutate: cancelDocumentConformityMutation } =
    useBrokerageFirmDocumentControllerCancelBrokerageFirmDocumentConformity({
      mutation: { onSuccess: () => refetchConformity() },
    });
  const { mutate: acceptAllDocumentConformityMutation } =
    useBrokerageFirmDocumentControllerAcceptAllBrokerageFirmDocuments({
      mutation: {
        onSuccess: () => refetchConformity(),
        onError: () => {
          toast({
            title: t('documentsListsPolicy.conformity.acceptAllErrorTitle'),
            description: t('documentsListsPolicy.conformity.acceptAllError'),
            variant: 'destructive',
          });
        },
      },
    });

  const onAcceptDocumentConformity = useCallback(
    (documentId: string) => acceptDocumentConformityMutation({ brokerageFirmId, documentId }),
    [acceptDocumentConformityMutation, brokerageFirmId],
  );
  const onRejectDocumentConformity = useCallback(
    (documentId: string, reason: string) =>
      rejectDocumentConformityMutation({ brokerageFirmId, documentId, data: { reason } }),
    [rejectDocumentConformityMutation, brokerageFirmId],
  );
  const onCancelDocumentConformity = useCallback(
    (documentId: string) => {
      const conformityId = conformityIdByDocumentId.get(documentId);
      if (conformityId) cancelDocumentConformityMutation({ brokerageFirmId, conformityId });
    },
    [cancelDocumentConformityMutation, conformityIdByDocumentId, brokerageFirmId],
  );
  const onAcceptAllDocumentConformity = useCallback(
    () => acceptAllDocumentConformityMutation({ brokerageFirmId }),
    [acceptAllDocumentConformityMutation, brokerageFirmId],
  );

  const extractionEnabled = useFeatureFlag('perm_ai_extraction', productId);
  const complianceEnabled = useFeatureFlag('perm_ai_validity', productId);
  const coherenceEnabled = useFeatureFlag('ops_ai_coherence');
  const { data: coherence } = useQuery({
    ...documentCoherenceControllerGetDocumentCoherenceQueryOptions(brokerageFirmId),
    enabled: coherenceEnabled,
    retry: false,
    refetchInterval: query =>
      query.state.data?.documents.some(({ status }) => status === 'PENDING') ? 2000 : false,
  });

  const { data: extractions } = useQuery({
    ...brokerageFirmDocumentExtractionControllerGetBrokerageFirmExtractionsQueryOptions(
      brokerageFirmId,
    ),
    enabled: extractionEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.extractions) return false;
      const hasPending = data.extractions.some(e => e.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const { data: validities } = useQuery({
    ...brokerageFirmDocumentValidityControllerGetBrokerageFirmValidityQueryOptions(brokerageFirmId),
    enabled: complianceEnabled,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data?.validities) return false;
      const hasPending = data.validities.some(c => c.status === 'PENDING');
      return hasPending ? 2000 : false;
    },
  });

  const getExtractionForDocument = useCallback(
    (documentId: string): GetDocumentExtractionOutput | undefined => {
      return extractions?.extractions?.find(e => e.documentId === documentId);
    },
    [extractions],
  );

  const complianceByDocumentId = useMemo(
    () =>
      new Map(
        (validities?.validities ?? [])
          .filter(c => c.status !== 'PENDING')
          .map(c => [c.documentId, c as AIValidityResult]),
      ),
    [validities],
  );

  const extractionQueryKey =
    brokerageFirmDocumentExtractionControllerGetBrokerageFirmExtractionsQueryOptions(
      brokerageFirmId,
    ).queryKey;

  const { mutate: submitFeedback, isPending: isFeedbackPending } =
    useDocumentExtractionControllerSubmitExtractionFeedback({
      mutation: {
        onSuccess: () => {
          toast({ description: t('documents.extraction.feedback.submitted') });
        },
        onError: () => {
          toast({
            description: t('documents.extraction.feedback.error'),
            variant: 'destructive',
          });
        },
      },
    });

  const validityQueryKey =
    brokerageFirmDocumentValidityControllerGetBrokerageFirmValidityQueryOptions(
      brokerageFirmId,
    ).queryKey;

  const { can } = useUserPermissions({
    productId,
    actions: [
      'OVERRIDE_DOCUMENT_VALIDITY',
      'GET_BROKERAGE_FIRM_DOCUMENT_FRAUD',
      'REVIEW_BROKERAGE_FIRM_DOCUMENT_FRAUD',
    ],
  });
  const canOverrideValidity = can.OVERRIDE_DOCUMENT_VALIDITY;
  const canViewFraud = showFraud && can.GET_BROKERAGE_FIRM_DOCUMENT_FRAUD;
  const canReviewFraud = canViewFraud && can.REVIEW_BROKERAGE_FIRM_DOCUMENT_FRAUD;

  const fraudQueryOptions =
    brokerageFirmDocumentControllerGetBrokerageFirmDocumentFraudQueryOptions(brokerageFirmId);
  const { data: fraudResults, refetch: refetchFraud } = useQuery({
    ...fraudQueryOptions,
    enabled: canViewFraud,
  });

  const { mutate: correctFraudVerdict, isPending: isCorrectingFraudVerdict } =
    useBrokerageFirmDocumentControllerCorrectBrokerageFirmDocumentFraudVerdict({
      mutation: {
        onSuccess: () => refetchFraud(),
        onError: error => {
          if (getKorintErrorStatus(error) === 409) void refetchFraud();
        },
      },
    });
  const handleCorrectFraudVerdict = useCallback(
    (documentId: string, correction: CorrectPolicyDocumentFraudVerdictInputBody) =>
      correctFraudVerdict({ brokerageFirmId, documentId, data: correction }),
    [correctFraudVerdict, brokerageFirmId],
  );

  const { mutate: overrideValidity, isPending: isValidityOverriding } =
    useBrokerageFirmDocumentValidityControllerOverrideDocumentValidity({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: validityQueryKey });
          toast({ description: t('documents.validity.overrideSuccess') });
        },
        onError: () => {
          toast({
            description: t('documents.validity.overrideError'),
            variant: 'destructive',
          });
        },
      },
    });

  const handleValidityOverride = useCallback(
    (documentId: string, newStatus: 'VALID' | 'INVALID', reason?: string) => {
      overrideValidity({ brokerageFirmId, documentId, data: { status: newStatus, reason } });
    },
    [overrideValidity, brokerageFirmId],
  );

  const sortedDocumentsLists = useMemo(
    () => [...documentsLists].sort((a, b) => (b.required ? 1 : 0) - (a.required ? 1 : 0)),
    [documentsLists],
  );

  const invalidateOnClassificationChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
  }, [queryClient, queryOptions.queryKey]);

  const onAllClassificationsComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    queryClient.invalidateQueries({ queryKey: extractionQueryKey });
    queryClient.invalidateQueries({ queryKey: validityQueryKey });
    queryClient.invalidateQueries({
      queryKey:
        documentCoherenceControllerGetDocumentCoherenceQueryOptions(brokerageFirmId).queryKey,
    });
    queryClient.invalidateQueries({ queryKey: fraudQueryOptions.queryKey });
  }, [
    queryClient,
    queryOptions.queryKey,
    extractionQueryKey,
    validityQueryKey,
    brokerageFirmId,
    fraudQueryOptions.queryKey,
  ]);

  const { classificationStates, processDocuments: processClassificationDocuments } =
    useDocumentsAutoClassification({
      getAccessToken,
      onDocumentValidated: invalidateOnClassificationChange,
      onAllComplete: onAllClassificationsComplete,
    });

  // Single batch upload mutation lifted to the module level — replaces the per-category
  // and per-batch upload hooks that were nested in the deprecated DocumentContainer
  // and BatchDocumentUploader.
  // Optimistic placeholders make the row visible in the "Non classés" folder
  // immediately — without them the file is invisible during S3 upload + the
  // backend's S3-event-to-projection lag, since onSuccess refetches before
  // the brokerage firm projection contains the new id.
  const { mutateAsync: handleUpload, isPending: isUploading } = useMutation({
    mutationFn: async (files: File[]) => {
      const uploadOne = (file: File) =>
        uploadDocumentsOnBrokerageFirm({
          brokerageFirmId,
          payload: {
            file,
            type: 'supporting_document',
            category: 'uncategorized',
            metadata: {},
            name: file.name,
          },
        });
      const withRetry = async (file: File) => {
        try {
          return await uploadOne(file);
        } catch {
          await new Promise(resolve => setTimeout(resolve, 500));
          return uploadOne(file);
        }
      };
      return Promise.all(files.map(withRetry));
    },
    onMutate: async files => {
      await queryClient.cancelQueries({ queryKey: queryOptions.queryKey });
      const previousData = queryClient.getQueryData(queryOptions.queryKey);
      queryClient.setQueryData(queryOptions.queryKey, old => {
        if (!old) return old;
        const optimisticDocs = files.map(file => ({
          id: `optimistic-${crypto.randomUUID()}`,
          name: file.name,
          path: '',
          createdAt: new Date().toISOString(),
          type: 'supporting_document' as const,
          category: 'uncategorized',
          signedUrl: '',
          metadata: [{ key: 'scanresult', value: '' }],
        }));
        return { ...old, data: [...old.data, ...optimisticDocs] };
      });
      return { previousData };
    },
    // No invalidate here on purpose: the SSE pipeline's `validating` event
    // (handled by `onDocumentValidated` → `invalidateOnClassificationChange`)
    // only fires after the backend has the doc, so refetching there cleanly
    // swaps the optimistic placeholder for the real record. Invalidating
    // earlier races the S3-event-to-projection lag and briefly removes the
    // optimistic row before the real one arrives.
    onSuccess: data => {
      const keys = data.map(d => d.key);
      if (keys.length > 0) processClassificationDocuments(keys);
      onUpload?.();
    },
    onError: (_error, _files, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(queryOptions.queryKey, context.previousData);
      }
      toast({
        description: 'Erreur lors du téléversement des documents',
        variant: 'destructive',
      });
    },
  });

  const viewableDocuments = useMemo(() => {
    if (!allDocuments) return [];

    const filtered = allDocuments.filter(d => {
      if (uploadMode === 'batch') {
        return d.type === 'supporting_document';
      }
      if (d.type !== 'supporting_document') return false;
      const category = d.category;
      return (
        category &&
        (relevantCategories.includes(String(category)) || isUnresolvedCategory(String(category)))
      );
    });
    const groups = new Map<string, typeof filtered>();
    filtered.forEach(doc => {
      const rawCategory = doc.category ? String(doc.category) : 'uncategorized';
      const category = isUnresolvedCategory(rawCategory) ? 'uncategorized' : rawCategory;
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)?.push(doc);
    });

    const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === 'uncategorized') return -1;
      if (b === 'uncategorized') return 1;
      return a.localeCompare(b);
    });

    const sorted = sortedGroups.flatMap(([, docs]) => docs);

    return sorted.map(doc => ({
      id: doc.id,
      name: doc.name,
      signedUrl: doc.signedUrl || '',
      createdAt: doc.createdAt,
      type: doc.type,
      category: doc.category,
      metadata: Object.fromEntries(
        Object.entries(doc.metadata).map(([key, value]) => [
          key,
          value === null || value === undefined
            ? undefined
            : typeof value === 'object'
              ? (value as Record<string, unknown>)
              : String(value),
        ]),
      ) as Record<string, string | number | boolean | Record<string, unknown> | undefined>,
    }));
  }, [allDocuments, relevantCategories, uploadMode]);

  const { selectedDocument, selectDocument, closeViewer } = useDocumentViewer(viewableDocuments);

  const selectedDocumentExtraction = selectedDocument
    ? getExtractionForDocument(selectedDocument.id)
    : undefined;

  const selectedDocumentValidity = selectedDocument
    ? complianceByDocumentId.get(selectedDocument.id)
    : undefined;

  const selectedDocumentStream = selectedDocument
    ? classificationStates.get(selectedDocument.id)
    : undefined;
  const selectedDocumentFraud = selectedDocument
    ? (fraudResults?.scores.find(({ documentId }) => documentId === selectedDocument.id) ??
      selectedDocumentStream?.fraud)
    : undefined;

  const selectedDocumentCoherence = coherenceEnabled
    ? coherence?.documents.find(({ documentId }) => documentId === selectedDocument?.id)
    : undefined;

  const handleExtractionFeedback = useCallback(
    (feedback: SubmitFeedbackBody) => {
      if (!selectedDocument) return;
      submitFeedback({ documentId: selectedDocument.id, data: feedback });
    },
    [selectedDocument, submitFeedback],
  );

  const { mutateAsync: deleteDocument } = useBrokerageFirmControllerDeleteDocuments({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
      },
      onError: () => {
        toast({
          description: 'Une erreur est survenue lors de la suppression du document',
          variant: 'destructive',
        });
      },
    },
  });

  const handleViewDocument: (document: DocumentViewerDocument) => void = selectDocument;

  const handleDeleteDocument = async (document: DocumentViewerDocument) => {
    await deleteDocument({
      brokerageFirmId,
      params: { ids: [document.id] },
    });
  };

  const renderBelowRow = useCallback(
    (doc: { id: string }) => {
      const conformity = conformityByDocumentId?.get(doc.id);
      if (conformity?.decision !== 'REJECTED') return null;
      return (
        <Group
          align="start"
          gap={2}
          className="mx-3 mb-2.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
        >
          <AlertCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
          <Text size="xs" className="text-red-800">
            <Text inline weight="semibold">
              {t('documentsListsPolicy.conformity.rejectedReasonPrefix')}
            </Text>{' '}
            {conformity.reason}
          </Text>
        </Group>
      );
    },
    [conformityByDocumentId, t],
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            {customCardSubtitle && <CardDescription>{customCardSubtitle}</CardDescription>}
          </div>
          {conformityByDocumentId && conformityByDocumentId.size > 0 && (
            <Group>
              <ConformityAcceptAllControl
                conformityByDocumentId={conformityByDocumentId}
                onAcceptAll={onAcceptAllDocumentConformity}
              />
            </Group>
          )}
        </CardHeader>
        <CardContent>
          <DocumentsCollection
            groups={getUploadedDocumentGroups({
              documents: viewableDocuments,
              categories: sortedDocumentsLists,
              translate: translateBrokerageFirmDocument(t),
            })}
            emptyCategories="all"
            classificationCategories={documentsLists}
            upload={{
              onDrop: handleUpload,
              isUploading,
              authorizedFormats,
              helpText: t(
                aiClassificationDisplayConfig?.enabled
                  ? 'fileInput.aiMaxFileSize'
                  : 'fileInput.uploadMaxFileSize',
              ),
              enforceAiSizeLimit: aiClassificationDisplayConfig?.enabled,
            }}
            onView={handleViewDocument}
            renderBadges={document => {
              const extraction = getExtractionForDocument(document.id);
              const validity = validities?.validities.find(
                ({ documentId }) => documentId === document.id,
              );
              const isProcessing = isDocumentProcessing({
                classification: classificationStates.get(document.id),
                extractionStatus: extraction?.status,
                validityStatus: validity?.status,
              });

              return document.id.startsWith('optimistic-') || isProcessing ? (
                <DocumentProcessingStatus
                  onOpen={document.signedUrl ? () => selectDocument(document) : undefined}
                />
              ) : null;
            }}
            onDelete={doc =>
              deleteDocument({
                brokerageFirmId,
                params: { ids: [doc.id] },
              })
            }
            renderBelowRow={renderBelowRow}
          />
        </CardContent>
      </Card>

      <DocumentViewer
        productId={productId}
        document={selectedDocument}
        documents={viewableDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
        onDelete={handleDeleteDocument}
        documentCustomCategorieMappings={documentsLists}
        uploadMode={uploadMode}
        extraction={selectedDocumentExtraction ?? null}
        extractionEnabled={extractionEnabled}
        onExtractionFeedback={extractionEnabled ? handleExtractionFeedback : undefined}
        isExtractionFeedbackPending={isFeedbackPending}
        validity={selectedDocumentValidity ?? null}
        validityEnabled={complianceEnabled}
        onValidityOverride={
          complianceEnabled && canOverrideValidity ? handleValidityOverride : undefined
        }
        isValidityOverriding={isValidityOverriding}
        coherence={selectedDocumentCoherence}
        coherenceEntity="brokerageFirm"
        conformity={selectedDocument ? conformityByDocumentId?.get(selectedDocument.id) : undefined}
        onAcceptConformity={onAcceptDocumentConformity}
        onRejectConformity={onRejectDocumentConformity}
        onCancelConformity={onCancelDocumentConformity}
        fraud={canViewFraud ? selectedDocumentFraud : undefined}
        fraudPending={canViewFraud && selectedDocumentStream?.status === 'checking_fraud'}
        onCorrectFraudVerdict={canReviewFraud ? handleCorrectFraudVerdict : undefined}
        isCorrectingFraudVerdict={isCorrectingFraudVerdict}
      />
    </>
  );
}
